import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Users, Award } from 'lucide-react';

const pillarsData = [
  {
    kanji: '義',
    name: 'Giustizia',
    samurai: 'Minamoto no Yoshitsune',
    chef: 'Massimo Bottura',
    values: ['Equità', 'Rispetto', 'Armonia'],
    quote: 'Ogni ingrediente è un cittadino del piatto, meritevole di pari dignità e rispetto.',
    description: 'La giustizia in cucina significa trattare ogni ingrediente con il rispetto che merita, valorizzando la provenienza e il lavoro di chi lo ha prodotto. Un chef giusto non spreca, non inganna, e riconosce il valore di ogni elemento del piatto.',
    color: 'border-red-500 bg-red-500/10'
  },
  {
    kanji: '勇',
    name: 'Coraggio',
    samurai: 'Miyamoto Musashi',
    chef: 'Ferran Adrià',
    values: ['Innovazione', 'Audacia', 'Determinazione'],
    quote: 'Il coraggio culinario non teme l\'innovazione, ma rispetta sempre la tradizione.',
    description: 'Il coraggio in cucina è l\'audacia di sperimentare nuove tecniche e sapori, mantenendo sempre il rispetto per gli ingredienti e le tradizioni. Un chef coraggioso sfida le convenzioni per creare esperienze uniche.',
    color: 'border-orange-500 bg-orange-500/10'
  },
  {
    kanji: '仁',
    name: 'Compassione',
    samurai: 'Takeda Shingen',
    chef: 'José Andrés',
    values: ['Empatia', 'Cura', 'Gentilezza'],
    quote: 'La compassione trasforma un pasto in un abbraccio, un ristorante in una casa.',
    description: 'La compassione in cucina si manifesta nell\'attenzione ai bisogni degli ospiti, nell\'inclusività del menu, nella cura per chi ha allergie o esigenze particolari. Un chef compassionevole nutre non solo il corpo, ma anche l\'anima.',
    color: 'border-green-500 bg-green-500/10'
  },
  {
    kanji: '礼',
    name: 'Rispetto',
    samurai: 'Oda Nobunaga',
    chef: 'Jiro Ono',
    values: ['Cortesia', 'Tradizione', 'Dignità'],
    quote: 'Il rispetto è il sale della cucina: invisibile ma essenziale per ogni sapore.',
    description: 'Il rispetto in cucina abbraccia la tradizione culinaria, la stagionalità degli ingredienti, il lavoro del team e l\'esperienza dell\'ospite. Un chef rispettoso onora il passato mentre costruisce il futuro.',
    color: 'border-blue-500 bg-blue-500/10'
  },
  {
    kanji: '誠',
    name: 'Onestà',
    samurai: 'Saigo Takamori',
    chef: 'Dan Barber',
    values: ['Trasparenza', 'Verità', 'Integrità'],
    quote: 'L\'onestà è l\'ingrediente segreto che rende ogni piatto autentico.',
    description: 'L\'onestà in cucina significa trasparenza sulla provenienza degli ingredienti, sui metodi di cottura, sui prezzi. Un chef onesto non nasconde, non inganna, ma condivide la verità del suo lavoro con orgoglio.',
    color: 'border-purple-500 bg-purple-500/10'
  },
  {
    kanji: '忠義',
    name: 'Devozione',
    samurai: '47 Ronin',
    chef: 'Thomas Keller',
    values: ['Dedizione', 'Passione', 'Fedeltà'],
    quote: 'La devozione trasforma la cucina da mestiere in missione, da lavoro in vocazione.',
    description: 'La devozione in cucina è la dedizione totale all\'arte culinaria, la fedeltà ai propri principi, la passione che non conosce compromessi. Un chef devoto serve con il cuore, non solo con le mani.',
    color: 'border-indigo-500 bg-indigo-500/10'
  },
  {
    kanji: '清',
    name: 'Purezza',
    samurai: 'Hattori Hanzo',
    chef: 'Alice Waters',
    values: ['Qualità', 'Semplicità', 'Autenticità'],
    quote: 'La purezza è l\'arte di rivelare l\'essenza, non di nasconderla sotto artifici.',
    description: 'La purezza in cucina è la ricerca della qualità assoluta degli ingredienti, la semplicità delle preparazioni che esalta i sapori naturali. Un chef puro non maschera, ma rivela la vera natura del cibo.',
    color: 'border-cyan-500 bg-cyan-500/10'
  }
];

export default function PillarsPage() {
  return (
    <div className="min-h-screen py-12 px-4">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="font-bushido text-5xl font-bold mb-6">
            I Sette Pilastri del <span className="gold-accent">Bushidō</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Ogni pilastro rappresenta un valore fondamentale del codice samurai applicato all'arte culinaria. 
            Questi principi guidano ogni recensione e valutazione nel sistema BushiFood.
          </p>
          <div className="flex justify-center mt-8 space-x-4">
            <Badge variant="secondary" className="px-4 py-2">
              <span className="font-kanji mr-2">武士道</span>
              Bushidō Culinario
            </Badge>
            <Badge variant="outline" className="px-4 py-2">
              <Star className="mr-2 h-4 w-4" />
              7 Principi Etici
            </Badge>
          </div>
        </div>

        {/* Pillars Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {pillarsData.map((pillar, index) => (
            <Card key={index} className={`pillar-card ${pillar.color} border-2`}>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="kanji-display text-6xl">
                    {pillar.kanji}
                  </div>
                  <Badge variant="secondary" className="text-sm">
                    Pilastro {index + 1}
                  </Badge>
                </div>
                <CardTitle className="font-bushido text-2xl mb-2">
                  {pillar.name}
                </CardTitle>
                <div className="flex flex-wrap gap-2">
                  {pillar.values.map((value, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {value}
                    </Badge>
                  ))}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Samurai & Chef */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-secondary flex items-center">
                      <Award className="mr-2 h-4 w-4" />
                      Samurai
                    </h4>
                    <p className="text-sm font-medium text-red-400">
                      {pillar.samurai}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-secondary flex items-center">
                      <Users className="mr-2 h-4 w-4" />
                      Chef
                    </h4>
                    <p className="text-sm font-medium text-green-400">
                      {pillar.chef}
                    </p>
                  </div>
                </div>

                {/* Quote */}
                <blockquote className="border-l-4 border-secondary pl-4 italic text-sm bg-muted/50 p-3 rounded-r">
                  "{pillar.quote}"
                </blockquote>

                {/* Description */}
                <CardDescription className="text-sm leading-relaxed">
                  {pillar.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center space-y-8">
          <Card className="max-w-2xl mx-auto bg-gradient-to-r from-muted/50 to-muted/30 border-secondary/30">
            <CardHeader>
              <CardTitle className="font-bushido text-2xl">
                Applica i Pilastri
              </CardTitle>
              <CardDescription>
                Usa questi principi per scrivere recensioni etiche e complete
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/recensione">
                <Button className="btn-samurai">
                  Scrivi Recensione
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/quiz">
                <Button variant="outline" className="btn-honor">
                  Quiz Accreditamento
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}